import Phaser from "phaser";
import {
  DirectionalText,
  type ButtonStyleToken,
  type NineSliceButton,
  type PanelStyleToken,
} from "@yjsgames/phaser-ui";
import { getTextDirection, t } from "../i18n/i18n.js";
import { THEME } from "../themes/activeTheme.js";
import { PALETTE, PALETTE_NUM } from "../themes/palette.js";
// 탭 콘텐츠 패널 inset 스펙의 SSoT는 순수 레이아웃 모듈(battleTabLayout) — 여기서 import해 9-slice 렌더에 쓴다.
import { TAB_CONTENT_BORDER, TAB_CONTENT_PADDING } from "./battleTabLayout.js";

// 기존 consumer(ShellFrame 등) 호환 — TAB_INNER_PADDING은 battleTabLayout SSoT를 re-export.
export { TAB_INNER_PADDING_X, TAB_INNER_PADDING_Y } from "./battleTabLayout.js";

/**
 * @yjsgames/phaser-ui 컴포넌트용 스타일 토큰 (DIP — 에셋키는 호출자 책임).
 * 버튼 텍스처(ui_btn)는 BootScene이 절차적으로 생성, 패널/탭바는 fantasy-tabs PNG 에셋 사용.
 */
/** 전 텍스트 공통 폰트 family — 테마 매니페스트(font.family) 소유, 미로드 시 시스템 sans-serif fallback. */
export const FONT_FAMILY = `"${THEME.font.family}", sans-serif`;

/** HiDPI 텍스트 해상도 — 고정 2로 모든 디스플레이에서 선명한 텍스트 보장. */
export const TEXT_RESOLUTION = 2;

const patchedTextFactories = new WeakSet<Phaser.GameObjects.GameObjectFactory>();

/** 직접 생성한 `Text`/`DirectionalText`에 공통 HiDPI 텍스트 해상도를 적용한다. */
export function applyTextResolution<T extends Phaser.GameObjects.Text>(text: T): T {
  text.setResolution(TEXT_RESOLUTION);
  return text;
}

/**
 * 씬의 표준 `add.text` 생성 경로에 HiDPI 텍스트 resolution을 일괄 적용한다.
 * Phaser UI 패키지와 FX 패키지도 대부분 이 factory를 쓰므로, 씬 시작 시 한 번만 패치하면 누락을 줄일 수 있다.
 */
export function installSceneTextResolutionPatch(scene: Phaser.Scene): void {
  const factory = scene.add;
  if (patchedTextFactories.has(factory)) return;
  const originalText = factory.text.bind(factory);
  factory.text = ((x, y, text, style) => applyTextResolution(originalText(x, y, text, style))) as typeof factory.text;
  patchedTextFactories.add(factory);
}

/**
 * 타이포 스케일 SSoT — fontSize 리터럴 금지(themeStatic 게이트가 차단), 이 토큰만 사용.
 * px 값은 레이아웃(패널 높이·행 간격)과 결합되어 있어 테마가 아닌 앱 소유.
 * 새 크기 추가는 기존 토큰으로 안 되는 "역할"이 생겼을 때만 — 임의 px 도입 금지.
 */
export const TYPO = {
  /** 9px — 초소형 버튼·뱃지·할인 표기 */
  micro: "9px",
  /** 10px — 보조 캡션·타일 라벨·디버그 행 */
  tiny: "10px",
  /** 11px — 기본 라벨 (칩·탭·리스트 행) — 가장 흔함 */
  caption: "11px",
  /** 12px — 본문·버튼 텍스트·설명 */
  body: "12px",
  /** 13px — 섹션 헤더·강조 셀 타이틀 */
  label: "13px",
  /** 15px — 중간 타이틀 */
  subtitle: "15px",
  /** 16px — 큰 본문 강조 (그리드 심볼·보상명) */
  large: "16px",
  /** 18px — 모달 타이틀 */
  title: "18px",
  /** 20px — 최대 강조 (등급 마크·닫기 글리프) */
  display: "20px",
} as const;

const BTN_BORDER = { left: 10, top: 10, right: 10, bottom: 10 } as const;

/** 슬롯 칩 (2줄 라벨 — 슬롯명 + 장착 모듈명). */
export const SLOT_CHIP_STYLE: ButtonStyleToken = {
  textureKey: "ui_btn",
  border: BTN_BORDER,
  width: 86,
  height: 42,
  textStyle: { fontFamily: FONT_FAMILY, fontSize: TYPO.caption, color: PALETTE.textPrimary, align: "center" },
};

/** 인벤토리 행 소형 버튼 (장착/강화). */
export const SMALL_BUTTON_STYLE: ButtonStyleToken = {
  textureKey: "ui_btn",
  border: BTN_BORDER,
  width: 56,
  height: 20,
  textStyle: { fontFamily: FONT_FAMILY, fontSize: TYPO.micro, color: PALETTE.textPrimary, align: "center" },
  denyTint: PALETTE_NUM.signalDanger,
};

/** 주요 액션 버튼 (상자 열기 / 환생 / 영구강화 / 저장 초기화 / 사운드). */
export const PRIMARY_BUTTON_STYLE: ButtonStyleToken = {
  textureKey: "ui_btn",
  border: BTN_BORDER,
  width: 120,
  height: 30,
  textStyle: { fontFamily: FONT_FAMILY, fontSize: TYPO.body, color: PALETTE.textPrimary, align: "center" },
  denyTint: PALETTE_NUM.signalDanger,
};

/** 탭 콘텐츠 배경 패널 — fantasy-tabs PNG 9-slice 에셋. border/contentPadding은 battleTabLayout SSoT. */
export const CONTENT_PANEL_STYLE: PanelStyleToken = {
  textureKey: "tab_content_panel_9slice",
  border: TAB_CONTENT_BORDER,
  contentPadding: TAB_CONTENT_PADDING,
};

/** 하단 토스트 전용 패널 — 탭 콘텐츠 패널과 구분되는 고대비 9-slice 에셋. */
export const TOAST_PANEL_STYLE: PanelStyleToken = {
  textureKey: "toast_panel_9slice",
  border: { left: 12, top: 12, right: 12, bottom: 12 },
  contentPadding: 12,
};

export const DISABLED_BUTTON_ALPHA = 0.72;
export const DISABLED_BUTTON_BG_ALPHA = 0.64;

/**
 * 버튼 활성/비활성 단일 관문 — 시각 dim(알파) + 클릭 억제·거부 피드백을 함께 전환한다.
 * 비활성 클릭 시 스타일 토큰 denyTint(signalDanger)가 있으면 빨간 틴트 + 흔들림이 자동 재생된다.
 */
export function setButtonEnabled(button: NineSliceButton, enabled: boolean): void {
  if (enabled) {
    button.setAlpha(1);
    button.clearBgTint();
    button.resetAlpha();
  } else {
    button.setAlpha(DISABLED_BUTTON_ALPHA);
    button.setBgTint(PALETTE_NUM.surfaceDisabled);
    button.setBgAlpha(DISABLED_BUTTON_BG_ALPHA);
  }
  button.setEnabled(enabled);
}

/** 등급별 표시 색 — SSoT는 global.tsv(`balance.RARITY_COLORS`). 인벤토리/도감/팔레트/유물/보상 팝업 공유. */
export { RARITY_COLORS as RARITY_COLOR } from "../data/balance.js";

/** 등급 표시 라벨 (현지화: rarity.<code>). 미존재 등급이면 key 원형 반환. */
export function rarityLabel(rarity: string): string {
  return t(`rarity.${rarity}`);
}

/** "#rrggbb" → Phaser tint number. 모듈/도감 색 → setStrokeStyle/tint 변환 공용(뷰별 중복 제거). */
export function hexToNum(hex: string): number {
  return parseInt(hex.replace("#", ""), 16);
}

/** 미발견 도감 항목 dim 색 — 테마 팔레트(textLocked) 소유. */
export const LOCKED_COLOR = PALETTE.textLocked;

/** 잠금 자물쇠 텍스처 키 — themeAssets 등록(그리드 잠금 셀·탭 잠금 배지·조각 해금 배지 공용 SSoT). */
export const LOCK_ICON_KEY = "icon_grid_lock";

/**
 * 현재 언어 방향에 맞춰 정렬/origin이 자동 설정되는 텍스트를 생성·씬에 추가한다 (@yjsgames/phaser-ui DirectionalText).
 * `align: "start"`면 LTR=좌측·RTL=우측 정렬. 방향에 따라 좌/우가 달라져야 하는 라벨에 사용한다.
 * (전면 치환이 아니라 방향 의존 라벨에만 도입 — 중앙 정렬 라벨은 기존 `scene.add.text` 그대로 둬도 무방.)
 * @param applyBidi `true`이면 Unicode Bidi 알고리즘(UAX #9)으로 RTL 라인을 표시 순서로 재배열한다.
 *   Phaser word-wrap 등으로 Bidi run이 라인 경계에서 끊기는 멀티라인 텍스트에만 사용한다.
 *   단순 단일 라인 라벨은 Canvas가 Bidi를 자체 처리하므로 기본값 `false`(no-op)가 적합하다.
 */
export function addDirectionalText(
  scene: Phaser.Scene,
  x: number,
  y: number,
  text: string,
  style: Phaser.Types.GameObjects.Text.TextStyle,
  align: "start" | "center" | "end" = "start",
  applyBidi = false,
): DirectionalText {
  const dt = new DirectionalText(scene, x, y, text, {
    dir: getTextDirection(),
    align,
    style,
    applyBidi,
  });
  applyTextResolution(dt);
  scene.add.existing(dt);
  return dt;
}
